import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { auth } from '../lib/auth'
import { PERMISSIONS, ROUTE_PERMISSIONS, NAVIGATION_PERMISSIONS } from '../lib/permissions'
import { GET_MY_PERMISSIONS } from '../graphql/permission-queries'

export const usePermissions = () => {
  const user = auth.getUser()
  const role = auth.getRole()
  const isAuthenticated = auth.isAuthenticated()

  const { data: permissionsData, loading: permissionsLoading, error: permissionsError } = useQuery(GET_MY_PERMISSIONS, {
    skip: !isAuthenticated,
    errorPolicy: 'all'
  })

  const permissions = useMemo(() => {
    try {
      // Apollo keeps the last `data` value even when `skip` becomes true.
      // Guard this so a newly logged-out user doesn't see stale permissions.
      const apiPermissions = isAuthenticated ? (permissionsData?.myPermissions || []) : []
      const permissionNames: string[] = apiPermissions.map((p: any) => p.name)
      const hasApiPermissions = permissionNames.length > 0

      // Single check: prefer live API permissions, fall back to permissions cached at login
      const check = (permission: string): boolean =>
        hasApiPermissions ? permissionNames.includes(permission) : auth.hasPermission(permission)

      const checkAny = (perms: string[]): boolean => perms.some(check)
      const checkAll = (perms: string[]): boolean => perms.every(check)

      return {
        hasPermission: check,
        hasAnyPermission: checkAny,
        hasAllPermissions: checkAll,

        canAccessRoute: (route: string): boolean => {
          let routePermissions = ROUTE_PERMISSIONS[route as keyof typeof ROUTE_PERMISSIONS]

          if (!routePermissions) {
            const matchingRoute = Object.keys(ROUTE_PERMISSIONS).find(pattern => {
              const regexPattern = pattern
                .replace(/:[^/]+/g, '[^/]+')
                .replace(/\//g, '\\/')
              return new RegExp(`^${regexPattern}$`).test(route)
            })
            if (matchingRoute) {
              routePermissions = ROUTE_PERMISSIONS[matchingRoute as keyof typeof ROUTE_PERMISSIONS]
            }
          }

          if (!routePermissions) return true
          return checkAny(routePermissions as unknown as string[])
        },

        canSeeNavigation: (navItem: string): boolean => {
          const navPermissions = NAVIGATION_PERMISSIONS[navItem as keyof typeof NAVIGATION_PERMISSIONS]
          if (!navPermissions) return true
          return checkAny(navPermissions as unknown as string[])
        },

        // Permission-based capability checks
        isAdmin: () => check(PERMISSIONS.USER_ASSIGN_ROLE) && check(PERMISSIONS.ROLE_ASSIGN_PERMISSIONS),
        canManageUsers: () => check(PERMISSIONS.USER_CREATE),
        canManageRoles: () => check(PERMISSIONS.ROLE_CREATE),
        canViewAllOrders: () => check(PERMISSIONS.ORDER_LIST_ALL) || check(PERMISSIONS.ORDER_DETAILS_ALL),
        canViewActiveOrders: () => checkAny([PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_DETAILS_TODAY, PERMISSIONS.ORDER_DETAILS_7DAYS]),
        canViewOrders: () => checkAny([PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_TODAY_PLANNED, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_LIST_7DAYS_PLANNED, PERMISSIONS.ORDER_LIST_ALL, PERMISSIONS.ORDER_DETAILS_TODAY, PERMISSIONS.ORDER_DETAILS_7DAYS, PERMISSIONS.ORDER_DETAILS_ALL]),
        canCreateOrders: () => check(PERMISSIONS.ORDER_CHECKOUT),
        canProcessPayments: () => check(PERMISSIONS.PAYMENT_CASH_CREATE) || check(PERMISSIONS.ORDER_CHECKOUT),
        canManageProducts: () => checkAny([PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE]),
        canViewProducts: () => checkAny([PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE]),
        canViewFinancialData: () => check(PERMISSIONS.REPORTS_VIEW),
        canStartPreparingOrders: () => check(PERMISSIONS.EXPEDITOR_ORDER_PREPARE_START),
        canCancelOrders: () => check(PERMISSIONS.ORDER_STATUS_CANCEL_PAID_TODAY),
        canViewMargins: () => check(PERMISSIONS.COSTING_MARGIN_VIEW),
        canViewOrderFinancials: () => check(PERMISSIONS.ORDER_FINANCIAL_VIEW),

        canRefundAmount: (amountCents: number): boolean => {
          const amountEuro = amountCents / 100
          return amountEuro <= 100 ? check(PERMISSIONS.REFUND_UPTO_100) : check(PERMISSIONS.REFUND_ABOVE_100)
        },

        canRefundOrderDate: (orderDate: string): boolean => {
          const daysDiff = Math.floor((Date.now() - new Date(orderDate).getTime()) / (1000 * 60 * 60 * 24))
          if (daysDiff === 0) return check(PERMISSIONS.REFUND_SAME_DAY)
          if (daysDiff <= 7) return check(PERMISSIONS.REFUND_UPTO_7DAYS)
          return check(PERMISSIONS.ORDER_DETAILS_ALL)
        },

        canProcessRefund: (amountCents: number, orderDate: string): boolean => {
          const amountEuro = amountCents / 100
          const hasAmountPermission = amountEuro <= 100 ? check(PERMISSIONS.REFUND_UPTO_100) : check(PERMISSIONS.REFUND_ABOVE_100)
          const daysDiff = Math.floor((Date.now() - new Date(orderDate).getTime()) / (1000 * 60 * 60 * 24))
          const hasDatePermission = daysDiff === 0
            ? check(PERMISSIONS.REFUND_SAME_DAY)
            : daysDiff <= 7 ? check(PERMISSIONS.REFUND_UPTO_7DAYS) : check(PERMISSIONS.ORDER_DETAILS_ALL)
          return hasAmountPermission && hasDatePermission
        },

        // Bookkeeping refund for webshop / platform orders — no amount or date tier (API rule).
        canRecordExternalRefund: () => check(PERMISSIONS.REFUND_RECORD_EXTERNAL),

        user,
        role,
        isAuthenticated: auth.isAuthenticated(),
        permissionsLoading,
        permissionsError,
        apiPermissions,
      }
    } catch (error) {
      return {
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,
        canAccessRoute: () => true,
        canSeeNavigation: () => true,
        isAdmin: () => false,
        canManageUsers: () => false,
        canManageRoles: () => false,
        canViewAllOrders: () => false,
        canViewActiveOrders: () => false,
        canViewOrders: () => false,
        canCreateOrders: () => false,
        canProcessPayments: () => false,
        canManageProducts: () => false,
        canViewProducts: () => false,
        canViewFinancialData: () => false,
        canStartPreparingOrders: () => false,
        canCancelOrders: () => false,
        canViewMargins: () => false,
        canViewOrderFinancials: () => false,
        canRefundAmount: () => false,
        canRefundOrderDate: () => false,
        canProcessRefund: () => false,
        canRecordExternalRefund: () => false,
        user: null,
        role: null,
        isAuthenticated: false,
        permissionsLoading: false,
        permissionsError: undefined,
        apiPermissions: [],
      }
    }
  }, [user, role, permissionsData, isAuthenticated])

  return permissions
}

// Hook for conditional rendering based on permissions
export const usePermissionRender = (permission: string | string[], requireAll = false) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions()

  if (typeof permission === 'string') {
    return hasPermission(permission)
  }

  return requireAll ? hasAllPermissions(permission) : hasAnyPermission(permission)
}

// Hook for role-based rendering
export const useRoleRender = (allowedRoles: string[]) => {
  const { role } = usePermissions()
  return role ? allowedRoles.includes(role.role) : false
}
